/**
 * AcCategory — 사고유형(/ac-datacenter/t/:slug)·기인물(/ac-datacenter/a/:slug) SEO 페이지.
 * 동결 스냅샷(categories.json) 렌더 → 프리렌더 가능. /en/ 경로는 강제 영어.
 */
import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { seoUrls } from '../../lib/seoUrl'
import { useTranslation } from 'react-i18next'
import { FiArrowLeft, FiSearch, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import { AC_I18N, type AcLang } from './acI18n'
import { loadCategories, findCategory, categoryPath, crossesOfIndustry, crossesOfType,
         type AcAxis, type AcCategory as AcCat } from './acCategories'
import { loadExamples } from './acExamples'
import { CaseCard, RiskSnapshot } from './AcDataCenter'


export default function AcCategory({ axis }: { axis: AcAxis }) {
  const { i18n } = useTranslation()
  const forcedEn = typeof window !== 'undefined' && window.location.pathname.startsWith('/en/')
  const lang: AcLang = forcedEn || i18n.language?.startsWith('en') ? 'en' : 'ko'
  const s = AC_I18N[lang]
  const params = useParams()
  // 교차 페이지(/i/{업종}/t/{재해형태})는 세그먼트가 둘 → 스냅샷 slug 규칙 '업종--재해형태' 로 합친다.
  const slug = axis === 'cross'
    ? `${params.industry || ''}--${params.type || ''}`
    : (params.slug || '')

  const { data: file } = useQuery({ queryKey: ['acCategories'], queryFn: loadCategories, staleTime: Infinity })
  const { data: works } = useQuery({ queryKey: ['acExamples', lang], queryFn: () => loadExamples(lang), staleTime: Infinity })
  const cat = useMemo(() => (file ? findCategory(file, axis, slug) : null), [file, axis, slug])

  // 경로 규칙은 acCategories.categoryPath 한 곳에서 — 라우트·canonical·내부링크가 어긋나지 않게.
  const path = cat ? categoryPath(cat)
    : axis === 'cross' ? `/ac-datacenter/i/${params.industry}/t/${params.type}`
    : `/ac-datacenter/${axis === 'type' ? 't' : axis === 'agent' ? 'a' : 'i'}/${slug}`
  // 트레일링 슬래시 통일(seoUrl) — 서버가 `/x` → `/x/` 로 301 하므로 canonical 도 슬래시로.
  const { canonical, ko: koUrl, en: enUrl } = seoUrls(path, lang as 'ko' | 'en')

  // 내부 링크망 — 프로그래매틱 SEO 의 핵심. 고아 페이지를 만들지 않는다.
  const siblings: AcCat[] = useMemo(() => {
    if (!file || !cat) return []
    if (cat.axis === 'industry') return crossesOfIndustry(file, cat.slug)          // 업종 → 그 업종의 재해형태들
    if (cat.axis === 'type') return crossesOfType(file, cat.slug).slice(0, 12)     // 재해형태 → 업종별 교차
    if (cat.axis === 'cross') {
      const ind = file.industries.find((i) => i.slug === cat.industry)
      const ty = file.types.find((t) => t.slug === cat.type)
      return [ind, ty].filter(Boolean) as AcCat[]                                   // 교차 → 상위 두 축
    }
    return []
  }, [file, cat])
  const label = cat ? cat.label[lang] : ''
  const intro = cat?.intro?.[lang] || ''
  const cases = cat?.cases?.[lang] || []
  const agg = cat?.agg?.[lang]
  const prevention = cat?.prevention?.[lang] || []

  const title = lang === 'en'
    ? `${label} — real accident cases & prevention | Heinrich`
    : `${label} 실제 사고사례·예방대책 | Heinrich`
  const desc = intro || (lang === 'en'
    ? `Real ${label} cases (AI-cleaned) with prevention tips and related risk assessments. Free, no sign-up.`
    : `${label} 실제 사고사례(AI 정제)와 예방수칙·관련 위험성평가. 무료·회원가입 없이.`)

  const jsonLd = useMemo(() => JSON.stringify({
    '@context': 'https://schema.org', '@type': 'Dataset',
    name: title, description: desc, url: canonical, isAccessibleForFree: true,
    keywords: cat?.keywords || [],
  }), [title, desc, canonical, cat])

  const relatedWorks = useMemo(
    () => (cat?.related_works || []).map((id) => works?.find((w) => w.id === id)).filter(Boolean),
    [cat, works],
  )

  if (file && !cat) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <ForceDarkTheme />
        <p className="text-ink-muted">{lang === 'en' ? 'Not found.' : '페이지를 찾을 수 없습니다.'}</p>
        <Link to="/ac-datacenter" className="mt-4 inline-block text-amber-400">← AC DataCenter</Link>
      </div>
    )
  }

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="ko" href={koUrl} />
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="x-default" href={koUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-surface-elevated/30">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(55% 45% at 50% 0%, rgba(245,158,11,0.09), transparent 70%)' }} />
        <div className="relative mx-auto max-w-5xl px-4 pt-14 pb-8">
          <Link to="/ac-datacenter" className="inline-flex items-center gap-1 text-sm text-ink-faint hover:text-amber-400">
            <FiArrowLeft size={14} /> AC DataCenter
          </Link>
          <h1 className="mt-3 text-2xl font-extrabold text-ink sm:text-4xl">
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">{label}</span>
          </h1>
          {intro && <p className="mt-3 max-w-2xl text-ink-muted">{intro}</p>}
          <Link to={`/ac-datacenter?q=${encodeURIComponent(label)}`} className="mt-5 inline-block">
            <button className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-400">
              <FiSearch size={15} /> {lang === 'en' ? 'Search this in cases' : '사고사례에서 검색'}
            </button>
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl space-y-8 px-4 py-10">
        {/* 분포(무LLM 집계) */}
        {agg && agg.by_accident_type && agg.by_accident_type.length > 0 && (
          <RiskSnapshot
            agg={{
              query: label, lang,
              // ⚠️ sample_size 를 0으로 넘기면 RiskSnapshot 이 `total = sample_size || 1` 로 계산해
              //    모든 항목이 100% 로 표시된다(실제로 전 페이지가 '약 100%'였다).
              //    동결 스냅샷에는 표본 크기가 없으므로 분포 합계를 표본으로 쓴다.
              sample_size: (agg.by_accident_type || []).reduce((a, b) => a + (b.count || 0), 0),
              by_accident_type: agg.by_accident_type, by_cause: agg.by_cause,
            }}
            s={s} lang={lang} />
        )}

        {/* 예방수칙 */}
        {prevention.length > 0 && (
          <div>
            <h2 className="mb-3 flex items-center gap-1.5 text-lg font-bold text-ink"><FiAlertTriangle className="text-amber-400" size={18} />{lang === 'en' ? 'Key prevention' : '핵심 예방수칙'}</h2>
            <ul className="grid gap-2 sm:grid-cols-2">
              {prevention.map((p, i) => (
                <li key={i} className="flex items-start gap-2 rounded-lg border border-surface-elevated/40 bg-surface-card p-3 text-[13.5px] text-ink-muted">
                  <FiCheckCircle className="mt-0.5 shrink-0 text-emerald-400" size={15} />{p}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 실제 사례 */}
        {cases.length > 0 && (
          <div>
            <h2 className="mb-3 text-lg font-bold text-ink">{s.casesTitle}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cases.map((c) => <CaseCard key={c.id} c={c} s={s} />)}
            </div>
          </div>
        )}

        {/* 관련 작업(내부링크) */}
        {relatedWorks.length > 0 && (
          <div className="border-t border-surface-elevated/30 pt-6">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-faint">{lang === 'en' ? 'Related tasks' : '관련 작업 위험성평가'}</h3>
            <div className="flex flex-wrap gap-2">
              {relatedWorks.map((w) => w && (
                <Link key={w.id} to={`${lang === 'en' ? '/en' : ''}/ac-datacenter/w/${w.id}`}
                  className="rounded-md border border-surface-elevated/50 bg-surface-card px-3 py-1.5 text-sm text-ink hover:border-amber-500/50 hover:text-amber-300">
                  {w.label[lang]}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* 내부 링크망 — 업종↔재해형태 교차. 프로그래매틱 SEO 는 링크가 끊기면(고아 페이지)
            크롤도 랭킹도 안 된다. 상·하위 축을 서로 연결해 크롤러가 전 페이지를 순회하게 한다. */}
        {siblings.length > 0 && (
          <div className="border-t border-surface-elevated/30 pt-6">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-faint">
              {cat?.axis === 'industry'
                ? (lang === 'en' ? 'Accident types in this industry' : '이 업종에서 자주 나는 사고')
                : cat?.axis === 'type'
                  ? (lang === 'en' ? 'This accident type by industry' : '업종별로 보기')
                  : (lang === 'en' ? 'Browse by axis' : '상위 분류로 보기')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {siblings.map((c) => (
                <Link key={`${c.axis}-${c.slug}`} to={`${lang === 'en' ? '/en' : ''}${categoryPath(c)}`}
                  className="rounded-md border border-surface-elevated/50 bg-surface-card px-3 py-1.5 text-sm text-ink hover:border-amber-500/50 hover:text-amber-300">
                  {c.label[lang]}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  )
}
